// ─── AuditPage.jsx ────────────────────────────────────────────────────────────
// Dedicated full-page audit trail showing all platform events, categorized
// and filterable by type, module, user, and date range.

import { db } from "./dbClient";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { downloadCSV, printToPDF } from "./exportUtils";

// ─── ACTION CATALOGUE ─────────────────────────────────────────────────────────
// Maps raw action strings → human label + category + icon + color key
const ACTION_META = {
  // Deletions / Destructive
  transactions_cleared_all:    { label: "All Transactions Deleted",        category: "Deletions",         icon: "🗑" },
  alerts_cleared_all:          { label: "All TM Alerts Deleted",            category: "Deletions",         icon: "🗑" },
  cases_cleared_all:           { label: "All Cases Deleted",                category: "Deletions",         icon: "🗑" },
  cases_bulk_deleted:          { label: "Cases Bulk Deleted",               category: "Deletions",         icon: "🗑" },
  tm_rule_deleted:             { label: "TM Rule Deleted",                  category: "Deletions",         icon: "🗑" },
  tm_rules_reset_to_defaults:  { label: "TM Rules Reset to Defaults",       category: "Deletions",         icon: "↺"  },
  watchlist_entry_removed:     { label: "Watchlist Entry Removed",          category: "Deletions",         icon: "🗑" },
  // Alert Dispositions
  alert_dismissed:             { label: "Alert Dismissed",                  category: "Alert Dispositions", icon: "✕" },
  alerts_bulk_dismissed:       { label: "Alerts Bulk Dismissed",            category: "Alert Dispositions", icon: "✕" },
  alerts_sent_to_case:         { label: "Alerts Sent to Case",              category: "Alert Dispositions", icon: "→" },
  // Cases
  cases_merged:                { label: "Cases Merged",                     category: "Cases",             icon: "⇌" },
  cases_bulk_closed:           { label: "Cases Bulk Closed",                category: "Cases",             icon: "◈" },
  cases_bulk_in_review:        { label: "Cases Moved to In Review",         category: "Cases",             icon: "◈" },
  cases_bulk_assigned:         { label: "Cases Bulk Assigned",              category: "Cases",             icon: "◈" },
  case_status_updated:         { label: "Case Status Updated",              category: "Cases",             icon: "◈" },
  case_assigned:               { label: "Case Assigned",                    category: "Cases",             icon: "◈" },
  case_closed:                 { label: "Case Closed",                      category: "Cases",             icon: "◈" },
  case_escalated:              { label: "Case Escalated",                   category: "Cases",             icon: "◈" },
  // TM / Rules
  tm_rule_updated:             { label: "TM Rule Updated",                  category: "TM Rules",          icon: "⇄" },
  tm_rule_created:             { label: "TM Rule Created",                  category: "TM Rules",          icon: "⇄" },
  tm_screening_run:            { label: "TM Screening Run",                 category: "TM Rules",          icon: "⇄" },
  // Compliance Filings
  ctr_filed:                   { label: "CTR Filed",                        category: "Filings",           icon: "◉" },
  sar_filed:                   { label: "SAR Filed",                        category: "Filings",           icon: "◉" },
  sar_submitted:               { label: "SAR Submitted",                    category: "Filings",           icon: "◉" },
  sar_draft_saved:             { label: "SAR Draft Saved",                  category: "Filings",           icon: "◉" },
  // Watchlist
  watchlist_entry_added:       { label: "Watchlist Entry Added",            category: "Watchlist",         icon: "🚫" },
  // KYC / Customer
  kyc_approved:                { label: "KYC Approved",                     category: "KYC",               icon: "✦" },
  kyc_rejected:                { label: "KYC Rejected",                     category: "KYC",               icon: "✦" },
  kyc_review_started:          { label: "KYC Review Started",               category: "KYC",               icon: "✦" },
  request_info:                { label: "KYC Info Requested",               category: "KYC",               icon: "✦" },
  kyc_status_updated:          { label: "KYC Status Updated",               category: "KYC",               icon: "✦" },
  sanctions_screening_completed: { label: "Sanctions Screening Completed",  category: "KYC",               icon: "🪪" },
  edd_review_completed:        { label: "EDD Review Completed",             category: "KYC",               icon: "✦" },
  customer_created:            { label: "Customer Created",                 category: "KYC",               icon: "👥" },
  customer_updated:            { label: "Customer Updated",                 category: "KYC",               icon: "👥" },
  // Customers (profile edits)
  customer_profile_updated:    { label: "Customer Profile Updated",         category: "Customers",         icon: "👤" },
  // Policies
  policy_document_created:     { label: "Policy Document Created",          category: "Policies",          icon: "📄" },
  policy_document_saved:       { label: "Policy Document Saved",            category: "Policies",          icon: "📄" },
  policy_document_deleted:     { label: "Policy Document Deleted",          category: "Policies",          icon: "🗑" },
  // Blockchain / Crypto Monitoring
  crypto_wallet_traced:        { label: "Wallet Traced",                    category: "Blockchain",        icon: "🔗" },
  crypto_tx_hash_looked_up:    { label: "Transaction Hash Looked Up",       category: "Blockchain",        icon: "🔗" },
  crypto_ai_risk_analysis:     { label: "AI Risk Analysis Run",             category: "Blockchain",        icon: "🤖" },
  crypto_osint_report:         { label: "OSINT Report Generated",           category: "Blockchain",        icon: "🔍" },
  crypto_node_added_to_graph:  { label: "Node Added to Graph",              category: "Blockchain",        icon: "⬡"  },
  // Third Party Due Diligence
  third_party_dd_created:      { label: "Third Party Added",                category: "Third Party DD",    icon: "🏢" },
  third_party_dd_approved:     { label: "Third Party Approved",             category: "Third Party DD",    icon: "✓" },
  third_party_dd_rejected:     { label: "Third Party Rejected",             category: "Third Party DD",    icon: "✕" },
  third_party_dd_under_review: { label: "Third Party Flagged for Review",   category: "Third Party DD",    icon: "⚠" },
  dd_questionnaire_created:    { label: "DD Questionnaire Created",         category: "Third Party DD",    icon: "📋" },
  dd_questionnaire_updated:    { label: "DD Questionnaire Updated",         category: "Third Party DD",    icon: "✏" },
  dd_questionnaire_submitted:  { label: "DD Questionnaire Submitted",       category: "Third Party DD",    icon: "📤" },
  dd_link_generated:           { label: "Client DD Link Generated",         category: "Third Party DD",    icon: "🔗" },
};

function getMeta(action = "") {
  if (ACTION_META[action]) return ACTION_META[action];
  // Fallback: derive from action string
  const label = action.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  if (/clear|delete|remov|reset/.test(action))  return { label, category: "Deletions",         icon: "🗑" };
  if (/dismiss/.test(action))                   return { label, category: "Alert Dispositions", icon: "✕" };
  if (/case/.test(action))                      return { label, category: "Cases",             icon: "◈" };
  if (/tm_|rule/.test(action))                  return { label, category: "TM Rules",          icon: "⇄" };
  if (/sar|ctr|fil/.test(action))               return { label, category: "Filings",           icon: "◉" };
  if (/watchlist/.test(action))                 return { label, category: "Watchlist",         icon: "🚫" };
  if (/kyc|onboard/.test(action))               return { label, category: "KYC",               icon: "✦" };
  if (/customer_profile/.test(action))          return { label, category: "Customers",         icon: "👤" };
  if (/customer/.test(action))                  return { label, category: "Customers",         icon: "👥" };
  if (/policy/.test(action))                    return { label, category: "Policies",          icon: "📄" };
  if (/crypto/.test(action))                    return { label, category: "Blockchain",        icon: "🔗" };
  if (/third_party_dd|dd_questionnaire|dd_link/.test(action)) return { label, category: "Third Party DD", icon: "🏢" };
  return { label, category: "Other", icon: "◦" };
}

const CATEGORY_CONFIG = {
  "Deletions":          { color: "#FF4444", bg: "rgba(255,68,68,0.1)",    border: "rgba(255,68,68,0.3)"    },
  "Alert Dispositions": { color: "#F0B429", bg: "rgba(240,180,41,0.1)",   border: "rgba(240,180,41,0.3)"   },
  "Cases":              { color: "#FF6B6B", bg: "rgba(255,107,107,0.1)",  border: "rgba(255,107,107,0.3)"  },
  "TM Rules":           { color: "#F0B429", bg: "rgba(240,180,41,0.08)",  border: "rgba(240,180,41,0.25)"  },
  "Filings":            { color: "#A78BFA", bg: "rgba(167,139,250,0.1)",  border: "rgba(167,139,250,0.3)"  },
  "Watchlist":          { color: "#FF8C00", bg: "rgba(255,140,0,0.1)",    border: "rgba(255,140,0,0.3)"    },
  "KYC":                { color: "#00C2FF", bg: "rgba(0,194,255,0.08)",   border: "rgba(0,194,255,0.25)"   },
  "Customers":          { color: "#4FC3F7", bg: "rgba(79,195,247,0.08)",  border: "rgba(79,195,247,0.25)"  },
  "Policies":           { color: "#81C784", bg: "rgba(129,199,132,0.08)", border: "rgba(129,199,132,0.25)" },
  "Blockchain":         { color: "#00D4FF", bg: "rgba(0,212,255,0.08)",  border: "rgba(0,212,255,0.25)"   },
  "Third Party DD":     { color: "#FF9E64", bg: "rgba(255,158,100,0.08)", border: "rgba(255,158,100,0.25)" },
  "Other":              { color: "#6B7A99", bg: "rgba(107,122,153,0.08)", border: "rgba(107,122,153,0.2)"  },
};

const CATEGORIES = ["All", "Deletions", "Alert Dispositions", "Cases", "TM Rules", "Filings", "Watchlist", "KYC", "Customers", "Policies", "Blockchain", "Other"];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function relativeTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function MetaChip({ label, value, color, bg }) {
  if (value === null || value === undefined || value === "") return null;
  const display = typeof value === "object" ? JSON.stringify(value) : String(value);
  return (
    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: bg || "rgba(107,122,153,0.1)", color: color || "#6B7A99", fontFamily: "'DM Mono',monospace", marginRight: 4, marginTop: 2, display: "inline-block" }}>
      {label}: {display}
    </span>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AuditPage({ company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();

  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [category,    setCategory]    = useState("All");
  const [search,      setSearch]      = useState("");
  const [userFilter,  setUserFilter]  = useState("All");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [expanded,    setExpanded]    = useState(new Set());
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 50;

  // ── Load logs ──────────────────────────────────────────────────────────────
  // Show all team members' actions for full audit trail visibility
  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    let q = db
      .from("audit_logs")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", false)
      .limit(2000);
    if (dateFrom) q = q.gte("created_at", dateFrom);
    if (dateTo)   q = q.lte("created_at", dateTo + "T23:59:59");
    q.then(({ data, error }) => {
      if (error) {
        console.warn("[Audit] load error:", error.message);
        setLogs([]);
      } else {
        setLogs(data || []);
      }
      setLoading(false);
    }).catch(err => {
      console.warn("[Audit] load failed:", err.message);
      setLogs([]);
      setLoading(false);
    });
  }, [company?.id, dateFrom, dateTo]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const allUsers = useMemo(() => {
    const names = [...new Set(logs.map(l => l.user_name || l.user_email).filter(Boolean))];
    return names.sort();
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter(l => {
      const meta = getMeta(l.action);
      if (category !== "All" && meta.category !== category) return false;
      if (userFilter !== "All") {
        const name = l.user_name || l.user_email || "";
        if (name !== userFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const haystack = [l.action, l.user_name, l.user_email, l.entity_name, l.entity_id, JSON.stringify(l.meta)].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, category, userFilter, search]);

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const counts = {};
    CATEGORIES.slice(1).forEach(c => { counts[c] = 0; });
    logs.forEach(l => {
      const cat = getMeta(l.action).category;
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [logs]);

  // ── Reset page on filter change ────────────────────────────────────────────
  useEffect(() => setPage(1), [category, search, userFilter]);

  // ── Expand/collapse detail ─────────────────────────────────────────────────
  function toggleExpand(id) {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  // ── Export ─────────────────────────────────────────────────────────────────
  function handleExportCSV() {
    downloadCSV(filtered.map(l => ({
      timestamp:   new Date(l.created_at).toLocaleString(),
      user:        l.user_name || l.user_email || "System",
      action:      l.action,
      category:    getMeta(l.action).category,
      entity:      l.entity_name || l.entity_id || "",
      module:      l.module || "",
      details:     l.meta ? JSON.stringify(l.meta) : "",
      old_value:   l.old_value ? JSON.stringify(l.old_value) : "",
      new_value:   l.new_value ? JSON.stringify(l.new_value) : "",
    })), `crossregguard_audit_${new Date().toISOString().slice(0,10)}.csv`);
  }

  // ── Full audit trail export (all records, unfiltered) ──────────────────────
  function handleExportAuditTrail() {
    // Fetch ALL records (not just current page/filter) for full tamper-evident export
    db.from("audit_logs")
      .select("*")
      .eq("company_id", company.id)
      .order("created_at", true)
      .then(({ data: all, error }) => {
        if (error) {
          console.warn("[Audit] export error:", error.message);
          return;
        }
        const rows = (all || []).map(l => ({
          timestamp:   new Date(l.created_at).toLocaleString(),
          user:        l.user_name || l.user_email || "System",
          action:      l.action,
          category:    getMeta(l.action).category,
          module:      l.module || "",
          entity:      l.entity_name || l.entity_id || "",
          details:     l.meta ? JSON.stringify(l.meta) : "",
          old_value:   l.old_value ? JSON.stringify(l.old_value) : "",
          new_value:   l.new_value ? JSON.stringify(l.new_value) : "",
        }));
        downloadCSV(rows, `crossregguard_full_audit_trail_${new Date().toISOString().slice(0,10)}.csv`);
      })
      .catch(err => console.warn("[Audit] export failed:", err.message));
  }

  function handleExportPDF() {
    const rows = filtered.map(l => {
      const meta = getMeta(l.action);
      const cfg  = CATEGORY_CONFIG[meta.category] || CATEGORY_CONFIG.Other;
      return `<tr>
        <td>${new Date(l.created_at).toLocaleString()}</td>
        <td>${l.user_name || l.user_email || "System"}</td>
        <td><span style="background:${cfg.bg};color:${cfg.color};padding:2px 6px;border-radius:4px;font-size:11px">${meta.label}</span></td>
        <td>${meta.category}</td>
        <td>${l.entity_name || l.entity_id || "—"}</td>
        <td style="font-size:10px">${l.meta ? JSON.stringify(l.meta) : "—"}</td>
      </tr>`;
    }).join("");
    printToPDF("Platform Audit Log", `
      <div class="section">
        <div class="section-title">Audit Trail — ${filtered.length} events${dateFrom ? ` · from ${dateFrom}` : ""}${dateTo ? ` to ${dateTo}` : ""}</div>
        <table>
          <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Category</th><th>Entity</th><th>Details</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`);
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inputStyle = {
    background: C.surfaceAlt, border: `1px solid ${C.border}`,
    borderRadius: 7, color: C.text, padding: "8px 12px",
    fontFamily: "'DM Mono',monospace", fontSize: 12, outline: "none",
    colorScheme: "dark",
  };

  return (
    <div style={{ maxWidth: 1100 }}>

      {/* ── Immutability Banner ───────────────────────────────────────────────── */}
      <div style={{
        background: "rgba(0,230,118,0.07)",
        border: "2px solid rgba(0,230,118,0.35)",
        borderRadius: 10,
        padding: "14px 20px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>🔒</span>
        <div>
          <div style={{ color: "#00E676", fontWeight: 700, fontSize: 13, fontFamily: "'DM Mono',monospace", marginBottom: 2 }}>
            Audit logs are immutable — records cannot be modified or deleted per 12 CFR 21.2(d) and FinCEN guidance (5-7 year retention).
          </div>
          <div style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
            All entries are append-only. Tampering with audit records is prohibited and may constitute a regulatory violation.
          </div>
        </div>
        <span style={{
          marginLeft: "auto",
          flexShrink: 0,
          background: "rgba(0,230,118,0.1)",
          border: "1px solid rgba(0,230,118,0.4)",
          borderRadius: 6,
          padding: "4px 12px",
          color: "#00E676",
          fontSize: 11,
          fontFamily: "'DM Mono',monospace",
          fontWeight: 700,
          letterSpacing: "0.08em",
          whiteSpace: "nowrap",
        }}>
          🔒 READ ONLY
        </span>
      </div>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 700 }}>
              {t('auditPage.title')}
            </div>
            <span style={{
              background: "rgba(107,122,153,0.12)",
              border: "1px solid rgba(107,122,153,0.3)",
              borderRadius: 5,
              padding: "3px 10px",
              fontSize: 10,
              color: C.textDim,
              fontFamily: "'DM Mono',monospace",
              fontWeight: 700,
              letterSpacing: "0.08em",
            }}>
              🔒 READ ONLY
            </span>
          </div>
          <div style={{ color: C.textDim, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
            {t('auditPage.subtitle')}
          </div>
          {/* E-029 / F-021: Immutability notice */}
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6,
            color: C.success, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
            🔒 {t('auditPage.tamperProofNotice')}
          </div>
          {/* E-030 / F-022: 7-year data retention notice */}
          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6,
            color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
            🗓 {t('auditPage.retentionNotice')} <strong style={{ color: C.textMid }}>{t('auditPage.retentionYears')}</strong> {t('auditPage.retentionCitation')}
            {t('auditPage.retentionArchival')}
          </div>
          {currentUser?.permission === "analyst" && (
            <div style={{ marginTop: 4, fontSize: 11, color: C.warn, fontFamily: "'DM Mono',monospace" }}>
              ⓘ {t('auditPage.analystOnlyNotice')}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={handleExportAuditTrail} style={{ ...inputStyle, padding: "8px 14px", cursor: "pointer", background: "rgba(0,230,118,0.08)", border: `1px solid rgba(0,230,118,0.35)`, color: C.success }}>
            ⬇ Export Audit Trail
          </button>
          <button onClick={handleExportCSV} style={{ ...inputStyle, padding: "8px 14px", cursor: "pointer" }}>
            ⬇ {t('auditPage.exportCSV')}
          </button>
          <button onClick={handleExportPDF} style={{ ...inputStyle, padding: "8px 14px", cursor: "pointer" }}>
            ⬇ {t('auditPage.exportPDF')}
          </button>
        </div>
      </div>

      {/* ── Stats row ────────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 24 }}>
        {[
          { label: t('auditPage.totalEvents'), value: logs.length, color: C.accent, isTotal: true },
          ...Object.entries(stats).filter(([, v]) => v > 0).map(([cat, count]) => ({
            label: cat, value: count, color: CATEGORY_CONFIG[cat]?.color || C.textDim, isTotal: false,
          })),
        ].map(s => (
          <div key={s.label} onClick={() => !s.isTotal && setCategory(s.label === category ? "All" : s.label)}
            style={{ background: C.surface, border: `1px solid ${s.label === category ? s.color : C.border}`, borderTop: `2px solid ${s.color}`, borderRadius: 10, padding: "12px 16px", cursor: !s.isTotal ? "pointer" : "default", transition: "border-color 0.15s" }}>
            <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.09em", marginBottom: 5, fontFamily: "'DM Mono',monospace" }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
        {/* Category tabs */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {CATEGORIES.map(cat => {
            const cfg = CATEGORY_CONFIG[cat] || {};
            const active = category === cat;
            return (
              <button key={cat} onClick={() => setCategory(cat)}
                style={{ padding: "5px 13px", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontWeight: active ? 700 : 400, transition: "all 0.12s",
                  background: active ? (cfg.bg || C.accentGlow) : "transparent",
                  border: `1px solid ${active ? (cfg.color || C.accent) : C.border}`,
                  color: active ? (cfg.color || C.accent) : C.textMid,
                }}>
                {cat}{cat !== "All" && stats[cat] ? ` (${stats[cat]})` : ""}
              </button>
            );
          })}
        </div>

        {/* Search + user + date row */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('auditPage.searchPlaceholder')}
            style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
          <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
            style={{ ...inputStyle, minWidth: 160 }}>
            <option value="All">{t('auditPage.allUsers')}</option>
            {allUsers.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ color: C.textDim, fontSize: 11 }}>{t('auditPage.dateFrom')}</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ ...inputStyle, padding: "7px 10px", fontSize: 11 }} />
            <span style={{ color: C.textDim, fontSize: 11 }}>{t('auditPage.dateTo')}</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ ...inputStyle, padding: "7px 10px", fontSize: 11 }} />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }}
                style={{ ...inputStyle, padding: "7px 12px", cursor: "pointer", fontSize: 11 }}>
                ✕ {t('auditPage.clearDates')}
              </button>
            )}
          </div>
          <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace", marginLeft: "auto" }}>
            {t('auditPage.eventCount', { count: filtered.length })}
          </span>
        </div>
      </div>

      {/* ── Event feed ───────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{ background: C.surface, borderRadius: 10, padding: "16px 20px", display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: C.border, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 12, borderRadius: 4, background: C.border, width: "40%", marginBottom: 8 }} />
                <div style={{ height: 10, borderRadius: 4, background: C.border, width: "70%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "60px 20px", textAlign: "center", color: C.textDim }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>◎</div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, marginBottom: 6 }}>{t('auditPage.noEventsFound')}</div>
          <div style={{ fontSize: 12 }}>
            {search || category !== "All" || userFilter !== "All" || dateFrom || dateTo
              ? t('auditPage.tryAdjustingFilters')
              : t('auditPage.eventsWillAppear')}
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {paginated.map(log => {
              const meta    = getMeta(log.action);
              const cfg     = CATEGORY_CONFIG[meta.category] || CATEGORY_CONFIG.Other;
              const isOpen  = expanded.has(log.id);
              const hasMeta = log.meta || log.old_value || log.new_value;

              return (
                <div key={log.id}
                  style={{ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, borderLeft: `3px solid ${cfg.color}`, overflow: "hidden" }}>

                  {/* ── Main row ── */}
                  <div style={{ padding: "14px 18px", display: "flex", gap: 14, alignItems: "flex-start", cursor: hasMeta ? "pointer" : "default" }}
                    onClick={() => hasMeta && toggleExpand(log.id)}>

                    {/* Icon badge */}
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: cfg.bg, border: `1px solid ${cfg.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                      {meta.icon}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        {/* Category badge */}
                        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: cfg.bg, color: cfg.color, fontFamily: "'DM Mono',monospace", fontWeight: 700, border: `1px solid ${cfg.border}`, letterSpacing: "0.05em" }}>
                          {meta.category.toUpperCase()}
                        </span>
                        {/* Action label */}
                        <span style={{ color: C.text, fontWeight: 600, fontSize: 13, fontFamily: "'DM Mono',monospace" }}>
                          {meta.label}
                        </span>
                      </div>

                      {/* Who + when */}
                      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.accentGlow, border: `1px solid ${C.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: C.accent, fontWeight: 700, flexShrink: 0 }}>
                            {(log.user_name || log.user_email || "S").charAt(0).toUpperCase()}
                          </div>
                          <span style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
                            {log.user_name || log.user_email || "System"}
                          </span>
                        </div>
                        {log.entity_name && (
                          <span style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
                            · {log.entity_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: timestamp + expand */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                      <span style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                      <span style={{ color: C.textDim, fontSize: 10 }}>
                        {relativeTime(log.created_at)}
                      </span>
                      {hasMeta && (
                        <span style={{ color: C.accent, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>
                          {isOpen ? `▲ ${t('auditPage.hideDetails')}` : `▼ ${t('auditPage.showDetails')}`}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Expanded detail panel ── */}
                  {isOpen && hasMeta && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 18px 14px", background: C.bg }}>
                      {/* Meta chips */}
                      {log.meta && typeof log.meta === "object" && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.09em", marginBottom: 6, fontFamily: "'DM Mono',monospace" }}>{t('auditPage.detailsLabel')}</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {Object.entries(log.meta).map(([k, v]) => (
                              <MetaChip key={k} label={k.replace(/_/g, " ")} value={v} color={C.textMid} bg={C.surfaceAlt} />
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Before / After */}
                      {(log.old_value || log.new_value) && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                          {log.old_value && (
                            <div>
                              <div style={{ color: C.danger, fontSize: 9, letterSpacing: "0.09em", marginBottom: 4, fontFamily: "'DM Mono',monospace" }}>{t('auditPage.beforeLabel')}</div>
                              <div style={{ background: "rgba(255,68,68,0.06)", border: "1px solid rgba(255,68,68,0.2)", borderRadius: 6, padding: "8px 12px", color: C.danger, fontFamily: "'DM Mono',monospace", fontSize: 11, wordBreak: "break-word" }}>
                                {typeof log.old_value === "object" ? JSON.stringify(log.old_value, null, 2) : String(log.old_value)}
                              </div>
                            </div>
                          )}
                          {log.new_value && (
                            <div>
                              <div style={{ color: C.success, fontSize: 9, letterSpacing: "0.09em", marginBottom: 4, fontFamily: "'DM Mono',monospace" }}>{t('auditPage.afterLabel')}</div>
                              <div style={{ background: "rgba(0,230,118,0.06)", border: "1px solid rgba(0,230,118,0.2)", borderRadius: 6, padding: "8px 12px", color: C.success, fontFamily: "'DM Mono',monospace", fontSize: 11, wordBreak: "break-word" }}>
                                {typeof log.new_value === "object" ? JSON.stringify(log.new_value, null, 2) : String(log.new_value)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* User details if available */}
                      {(log.user_email || log.module || log.entity_id || log.new_value?.account_number || log.details?.account_number) && (
                        <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
                          {log.user_email && <MetaChip label="email" value={log.user_email} color={C.textDim} bg={C.surfaceAlt} />}
                          {log.module     && <MetaChip label="module" value={log.module} color={C.textDim} bg={C.surfaceAlt} />}
                          {log.entity_id  && <MetaChip label="entity id" value={log.entity_id} color={C.textDim} bg={C.surfaceAlt} />}
                          {(log.new_value?.account_number || log.details?.account_number) && <MetaChip label="account" value={log.new_value?.account_number || log.details?.account_number} color={C.accent} bg={`${C.accent}15`} />}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "20px 0 4px", flexWrap: "wrap" }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: page === 1 ? C.textDim : C.text, cursor: page === 1 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>← {t('auditPage.prevPage')}</button>
              {Array.from({ length: Math.min(totalPages, 9) }, (_, i) => {
                // Show pages near current
                let p;
                if (totalPages <= 9) { p = i + 1; }
                else if (page <= 5)  { p = i + 1; if (p > 7) p = p === 8 ? "…" : totalPages; }
                else if (page >= totalPages - 4) { p = totalPages - 8 + i; if (p < 1) p = p === 0 ? "…" : 1; }
                else { const offsets = [-3,-2,-1,0,1,2,3]; p = i < 2 ? (i === 0 ? 1 : "…") : i > 6 ? (i === 8 ? totalPages : "…") : page + offsets[i - 2]; }
                if (p === "…") return <span key={`e${i}`} style={{ color: C.textDim, padding: "0 4px" }}>…</span>;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ padding: "6px 11px", borderRadius: 6, border: `1px solid ${p === page ? C.accent : C.border}`, background: p === page ? C.accentGlow : "transparent", color: p === page ? C.accent : C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: p === page ? 700 : 400 }}>{p}</button>
                );
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: page === totalPages ? C.textDim : C.text, cursor: page === totalPages ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>{t('auditPage.nextPage')} →</button>
              <span style={{ color: C.textDim, fontSize: 11, marginLeft: 8 }}>
                {t('auditPage.paginationInfo', { total: filtered.length, page, totalPages })}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
